'use client';

import { Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

import { useTranslations } from '@/components/language-provider';
import { VenuesExplorer } from '@/components/venues-near-me';
import type { HomeVenuesMessages } from '@/components/venues-near-me';

export default function VenuesPage() {
  const { messages } = useTranslations();

  return (
    <Suspense fallback={<Skeleton />}> 
      <VenuesPageContent messages={messages.homeVenues} backLabel={messages.homeVenues.backToHome ?? 'Back to home'} />
    </Suspense>
  );
}

function VenuesPageContent({
  messages,
  backLabel,
}: {
  messages: HomeVenuesMessages;
  backLabel: string;
}) {
  const searchParams = useSearchParams();
  const initialQuery = searchParams.get('q') ?? '';
  const focusParam = searchParams.get('venue');
  const parsedFocusId = focusParam ? Number(focusParam) : NaN;
  const focusVenueId = Number.isFinite(parsedFocusId) ? parsedFocusId : null;

  return (
    <main className="mx-auto flex max-w-6xl flex-col gap-10 px-6 py-16 lg:px-8">
      <div>
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-sm font-semibold text-primary transition hover:text-primary/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <span aria-hidden="true">←</span>
          {backLabel}
        </Link>
        <Link
          href="/venues/locations"
          className="ml-4 inline-flex items-center gap-2 text-sm font-semibold text-primary transition hover:text-primary/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          Browse by location
        </Link>
      </div>
      <VenuesExplorer messages={messages} initialQuery={initialQuery} focusVenueId={focusVenueId} />
    </main>
  );
}

function Skeleton() {
  return (
    <main className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-16 lg:px-8">
      <div className="h-4 w-24 animate-pulse rounded-full bg-muted" />
      <div className="h-10 w-64 animate-pulse rounded-full bg-muted" />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="h-48 animate-pulse rounded-3xl bg-muted" />
        ))}
      </div>
    </main>
  );
}
